<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { ProductFeature } from '@/api/generated/api.enum';
import { useBillingState } from '@/composables/billing/useBillingState';
import CustomAccordion from '@/core/elements/CustomAccordion.vue';
import IconButton from '@/core/elements/IconButton.vue';
import UserAccessRightsForm from '@/core/forms/UserAccessRightsForm.vue';
import { IUserWithPermissions } from '@/interfaces';
import { useContainerTranslations } from '@/schoolClasses/useContainerTranslations';
import { EIcon } from 'common/enum/misc.enum';
import { computed, onMounted, ref, toRefs, useTemplateRef, watch } from 'vue';
import InfoTooltip from '../elements/InfoTooltip.vue';
import UserCard from '../UserCard.vue';

const emit = defineEmits<{
  'click:remove': [value: IUserWithPermissions];
  'update:user': [value: IUserWithPermissions];
  'update:isValid': [value: boolean];
}>();

const props = withDefaults(
  defineProps<{
    user: IUserWithPermissions;
    availableSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
    availableSchoolSubjectGroups: Components.Schemas.SchoolSubjectGroupDto[];
    disabled?: boolean;
    hideRemoveButton?: boolean;
    isNewAccessRight?: boolean;
    mode?: 'archive' | 'edit';
    startOpen?: boolean;
  }>(),
  {
    hideRemoveButton: false,
    mode: 'edit',
    startOpen: false,
  },
);
const { user, disabled, hideRemoveButton, mode, startOpen } = toRefs(props);

const { hasFeature, isBillingStatusLoading } = useBillingState();
const { tContainer } = useContainerTranslations();
const isAccessRightsLocked = computed<boolean>(() => !hasFeature(ProductFeature.accessRights));

const accordionRef = useTemplateRef('accordionRef');
const userInternal = ref<IUserWithPermissions | null>(null);

const decollapse = (): void => {
  accordionRef.value?.show();
};

const collapse = (): void => {
  accordionRef.value?.hide();
};

onMounted(() => {
  if (startOpen.value) accordionRef.value?.show();
});

defineExpose({
  decollapse,
  collapse,
});

watch(
  user,
  () => {
    userInternal.value = user.value;
  },
  { immediate: true },
);
</script>

<template>
  <CustomAccordion ref="accordionRef" :can-clear="true" class="mb-sm">
    <template #header>
      <div class="flex w-full min-w-0 justify-between pr-sm">
        <UserCard :user="user.user" class="min-w-0 flex-1">
          <div v-if="user.isInvitation && !user.isPendingInvitation" class="text-muted">
            {{ $t('user.table.invitationPending') }}
          </div>
          <div v-else-if="user.isPendingInvitation" class="text-muted flex items-center gap-xs font-normal">
            <span>{{ $t('general.willBeInvited') }}</span>
            <InfoTooltip :tooltip-text="tContainer('teamMemberWillBeInvitedDescription')" content-style="w-[360px]" />
          </div>
          <div v-else :title="user.user.email" class="min-w-0 max-w-full truncate">{{ user.user.email }}</div>
        </UserCard>

        <IconButton
          v-if="!hideRemoveButton"
          :icon-name="EIcon.X"
          theme="secondary"
          class="h-button w-button mt-0.5"
          @click.stop="emit('click:remove', user)"
        />
      </div>
    </template>
    <template #items>
      <div class="pr-md pb-md pl-md md:pl-[116px]">
        <UserAccessRightsForm
          v-if="userInternal"
          :mode="mode"
          :user="userInternal"
          :available-school-subjects="availableSchoolSubjects"
          :available-school-subject-groups="availableSchoolSubjectGroups"
          :disabled="disabled"
          :is-access-rights-locked="isAccessRightsLocked"
          :is-billing-status-loading="isBillingStatusLoading"
          :is-new-access-right="isNewAccessRight"
          @update:user="(val) => emit('update:user', val)"
          @update:is-valid="(val) => emit('update:isValid', val)"
        />
      </div>
    </template>
  </CustomAccordion>
</template>
